import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Base64, decode, encodeURI, encodeURL } from "js-base64";

import { apiFetch } from "./client";
import { bomDetailQueryKey, bomListQueryKey, creditsAvailableQueryKey } from "./queryKeys";

function transformBomListItem(bomListItem) {
  return {
    BOMID: Base64.decode(bomListItem.id),
    BOM_State: bomListItem.status,
    TS: dayjs(bomListItem.created_at).unix(),
    SubmitTS: dayjs(bomListItem.created_at).unix(),
    Qualified: bomListItem.qualified ? "Y" : "N",
    QualPref: bomListItem.qualification_preference,
    QualUnder: bomListItem.qualified_under,
    Hts: bomListItem.fg.hts,
    Part: bomListItem.fg.part_number,
    Desc: bomListItem.fg.description,
    Value: bomListItem.fg.value,
    Origin: bomListItem.fg.Origin,
  };
}

function transformBomList(response) {
  if (response && Array.isArray(response) && response.length > 0) {
    const boms = [...response];
    boms.sort((bomA, bomB) => {
      const timeA = dayjs(bomA.updated_at);
      const timeB = dayjs(bomB.updated_at);
      if (timeA.isBefore(timeB)) {
        return 1;
      }
      if (timeA.isAfter(timeB)) {
        return -1;
      }
      return 0;
    });
    return boms.map(transformBomListItem);
  }
  return [];
}

function transformBom(response) {
  if (!response) {
    return {};
  }
  let submitTimeStamp = "";
  const submittedAt = response.submitted_at;
  if (submittedAt) {
    submitTimeStamp = dayjs(submittedAt).unix();
  }
  const transformed = {
    BOMID: decode(response.id),
    BOM_State: response.status,
    SubmitTS: submitTimeStamp,
    Qualified: response.qualified ? "Y" : "N",
    QualPref: response.qualification_preference,
    QualUnder: response.qualified_under,
    QualNotes: response.qualification_notes || "",
    FG: {
      Hts: response.fg.hts,
      Part: response.fg.part_number,
      Desc: response.fg.description,
      Value: response.fg.transaction_value,
      NetCost: response.fg.net_cost,
      TotalCost: response.fg.total_cost,
      Currency: response.currency,
      Origin: response.fg.origin,
      AutoType: response.fg.auto_type,
      AutoTimeframe: response.fg.auto_timeframe,
    },
    FG_Qs_Length: null,
    FG_Qs: response.fg.questions.map((question) => ({
      RuleID: question.rule_id,
      RuleLabel: question.rule_label,
      Question: question.question,
      AnsLabel: question.answer_label,
      Ans: question.accepted ? "Y" : "N",
    })),
    Length: null,
    Comp_Qs_Length: null,
    Comp_Qs: response.components.flatMap((component) =>
      component.questions.flatMap((question) => ({
        RuleID: question.component_id,
        Question: question.question,
        AnsLabel: question.answer_label,
        Ans: question.accepted ? "Y" : "N",
      })),
    ),
    Components: response.components.map((component, idx) => ({
      LineItem: String(idx + 1),
      Category: component.category,
      Hts: component.hts,
      Part: component.part_number,
      Desc: component.description,
      Origin: component.origin,
      Qty: component.qty,
      Unit: component.units,
      Cost: component.cost,
      USMCA: component.usmca ? "Y" : "N",
      Originating: component.originating ? "Y" : "N",
    })),
  };
  return transformed;
}

export function useListBoms(options = {}) {
  return useQuery({
    queryKey: bomListQueryKey,
    queryFn: () => apiFetch("/extras/api/v2/boms"),
    select: transformBomList,
    ...options,
  });
}

export function useGetBom(bomId, options = {}) {
  return useQuery({
    queryKey: bomDetailQueryKey(bomId),
    queryFn: () => apiFetch(`/extras/api/v2/boms/${encodeURI(bomId)}`),
    select: transformBom,
    ...options,
  });
}

function invalidateBom(queryClient, bomId) {
  queryClient.invalidateQueries({ queryKey: bomDetailQueryKey(bomId) });
  queryClient.invalidateQueries({ queryKey: bomListQueryKey });
}

export function useCreateBom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch("/extras/api/v2/boms", { method: "POST", body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bomListQueryKey });
    },
  });
}

export function useUpdateBom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bomId, body }) => apiFetch(`/extras/api/v2/boms/${encodeURL(bomId)}`, { method: "PATCH", body }),
    onSuccess: (_data, { bomId }) => {
      invalidateBom(queryClient, bomId);
    },
  });
}

export function useDeleteBom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bomId) => apiFetch(`/extras/api/v2/boms/${encodeURL(bomId)}`, { method: "DELETE" }),
    onSuccess: (_data, bomId) => {
      invalidateBom(queryClient, bomId);
    },
  });
}

export function useSubmitFinishedGood() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bomId) => apiFetch(`/extras/api/v2/boms/submit/${encodeURL(bomId)}`, { method: "POST" }),
    onSuccess: (_data, bomId) => {
      invalidateBom(queryClient, bomId);
    },
  });
}

export function useSubmitComponents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bomId) => apiFetch(`/extras/api/v2/boms/submit/components/${encodeURL(bomId)}`, { method: "POST" }),
    onSuccess: (_data, bomId) => {
      invalidateBom(queryClient, bomId);
    },
  });
}

export function useCreateComponents() {
  return useMutation({
    mutationFn: ({ BOMID, Components }) => apiFetch("/comps", { method: "POST", body: { BOMID, Components } }),
  });
}

export function useAnswerFinishedGoodQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bomId, body }) => apiFetch(`/extras/api/v2/boms/submit/fg-questions/${encodeURL(bomId)}`, { method: "POST", body }),
    onSuccess: (_data, { bomId }) => {
      invalidateBom(queryClient, bomId);
    },
  });
}

export function useAnswerComponentQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bomId, body }) => apiFetch(`/extras/api/v2/boms/submit/component-questions/${encodeURL(bomId)}`, { method: "POST", body }),
    onSuccess: (_data, { bomId }) => {
      invalidateBom(queryClient, bomId);
    },
  });
}

export function useCloseBom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bomId) => apiFetch(`/extras/api/v2/boms/close/${encodeURL(bomId)}`, { method: "POST" }),
    onSuccess: (_data, bomId) => {
      invalidateBom(queryClient, bomId);
      queryClient.invalidateQueries({ queryKey: creditsAvailableQueryKey });
    },
  });
}
